import axios from "axios";
import { DEFAULT_AXIOS_CONFIG } from "./userService";
import { mergeAuthHeaders } from "./authTokenStore";
import { DEFAULT_CHAT_MESSAGE_PAGE_SIZE, normalizeChatMessagePageLimit } from "../constants/chat";
import { normalizeChatMessage, normalizeTimestamp } from "../utils/messageUtils";
import { resolveGatewayBackedServiceBaseUrl } from "./serviceBaseUrl";

const CHAT_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_CHAT_SERVICE_URL);

const withAuth = (config = {}) => ({
  ...DEFAULT_AXIOS_CONFIG,
  ...config,
  headers: mergeAuthHeaders(config.headers),
});

const withJsonAuth = () => withAuth({
  headers: { "Content-Type": "application/json" },
});

// Creates a direct chat with another user (friend)
// The API expects a JSON array of UUID userIds, e.g. ["11111111-1111-4111-8111-111111111111"]
// Returns chat object: { id, participantIds, contextType, ... }
const createDirectChat = async (friendUserId) => {
  if (!friendUserId) throw new Error("friendUserId is required");
  const payload = [String(friendUserId)];
  const response = await axios.post(
    `${CHAT_SERVICE_URL}/api/chat/create`,
    payload,
    withJsonAuth()
  );
  return response.data;
};

const createGroupChat = async (participantIds) => {
  if (!Array.isArray(participantIds)) {
    throw new Error("participantIds must be an array");
  }
  const normalizedParticipantIds = Array.from(new Set(
    participantIds
      .filter((participantId) => participantId !== undefined && participantId !== null)
      .map((participantId) => String(participantId).trim())
      .filter(Boolean)
  ));
  if (normalizedParticipantIds.length === 0) {
    throw new Error("participantIds are required");
  }
  const response = await axios.post(
    `${CHAT_SERVICE_URL}/api/chat/groups`,
    { participantIds: normalizedParticipantIds },
    withJsonAuth()
  );
  return response.data;
};

const getMyChats = async () => {
  const response = await axios.get(
    `${CHAT_SERVICE_URL}/api/chat/list`,
    withAuth()
  );
  return Array.isArray(response.data) ? response.data : [];
};

const getChatMessages = async (chatId) => {
  if (!chatId) throw new Error("chatId is required");
  const response = await axios.get(
    `${CHAT_SERVICE_URL}/api/chat/${encodeURIComponent(chatId)}/messages`,
    withAuth()
  );
  return Array.isArray(response.data) ? response.data : [];
};

const getChatMessagesPage = async (chatId, { limit = DEFAULT_CHAT_MESSAGE_PAGE_SIZE, cursor } = {}) => {
  if (!chatId) throw new Error("chatId is required");
  const pageLimit = normalizeChatMessagePageLimit(limit);
  const params = { limit: pageLimit };
  if (cursor) {
    params.cursor = cursor;
  }
  const response = await axios.get(
    `${CHAT_SERVICE_URL}/api/chat/${encodeURIComponent(chatId)}/messages/page`,
    { ...withAuth(), params }
  );
  const data = response?.data || {};
  const totalCountValue = Number(data.totalCount);
  const totalCount = Number.isFinite(totalCountValue) ? totalCountValue : null;
  return {
    items: Array.isArray(data.items) ? data.items : [],
    hasMore: Boolean(data.hasMore),
    nextCursor: data.nextCursor || null,
    pageSize: typeof data.pageSize === "number" ? data.pageSize : pageLimit,
    totalCount,
  };
};

const getChatMessageCount = async (chatId) => {
  if (!chatId) throw new Error("chatId is required");
  const response = await axios.get(
    `${CHAT_SERVICE_URL}/api/chat/${encodeURIComponent(chatId)}/messages/count`,
    withAuth()
  );
  const total = Number(response?.data?.totalCount);
  return Number.isFinite(total) ? total : 0;
};

const normalizeSearchQuery = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/\s+/g, " ").trim().slice(0, 200);
};

const toPlainText = (html) => {
  if (!html) {
    return "";
  }
  const text = String(html);
  return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const searchChatMessages = async (chatId, query, { limit = 20, pageSize = 50, maxPages = 6 } = {}) => {
  if (!chatId) throw new Error("chatId is required");
  const normalizedQuery = normalizeSearchQuery(query);
  if (!normalizedQuery) {
    return { results: [], hasMore: false };
  }

  const needle = normalizedQuery.toLowerCase();
  let cursor = null;
  let hasMore = true;
  let pagesFetched = 0;
  let matches = [];

  while (hasMore && pagesFetched < maxPages && matches.length < limit) {
    // eslint-disable-next-line no-await-in-loop
    const page = await getChatMessagesPage(chatId, { limit: pageSize, cursor });
    const items = Array.isArray(page?.items) ? page.items : [];
    const normalized = items.map((msg) => normalizeChatMessage(msg, { ensureSeenByArray: true }));
    const pageMatches = normalized.filter((msg) => toPlainText(msg?.content).toLowerCase().includes(needle));
    if (pageMatches.length) {
      matches = matches.concat(pageMatches);
    }
    hasMore = Boolean(page?.hasMore) && Boolean(page?.nextCursor);
    cursor = page?.nextCursor || null;
    pagesFetched += 1;
  }

  const bounded = matches.slice(0, limit);
  return {
    results: bounded,
    hasMore: hasMore || matches.length > limit,
  };
};

const createLatestMessageFallback = (chatId) => ({
  id: null,
  chatId: chatId !== undefined && chatId !== null ? String(chatId) : null,
  content: "No messages yet",
  timestamp: null,
});

const normalizeLatestMessageResponse = (payload, chatId) => {
  if (payload && typeof payload === "object") {
    const normalized = normalizeChatMessage(payload, { ensureSeenByArray: true }) || {};
    const rawContent = typeof normalized.content === "string" ? normalized.content : "";
    const trimmedContent = rawContent.trim();
    return {
      ...normalized,
      chatId: normalized.chatId ?? (chatId !== undefined && chatId !== null ? String(chatId) : null),
      content: trimmedContent || "No messages yet",
    };
  }
  const fallbackRaw =
    typeof payload === "string"
      ? payload
      : payload === undefined || payload === null
        ? ""
        : String(payload);
  const trimmed = fallbackRaw.trim();
  return {
    ...createLatestMessageFallback(chatId),
    content: trimmed || "No messages yet",
  };
};

const hasTimestampMetadata = (message) => {
  if (!message || typeof message !== "object") {
    return false;
  }
  const resolved = normalizeTimestamp(
    message.timestamp ?? message.createdAt ?? message.sentAt ?? message.updatedAt
  );
  if (resolved) {
    message.timestamp = resolved;
    return true;
  }
  return false;
};

const hydrateLatestMessageFromHistory = async (chatId, baseMessage = null) => {
  try {
    const page = await getChatMessagesPage(chatId, { limit: 1 });
    const items = Array.isArray(page?.items) ? page.items : [];
    const fallbackRaw =
      items.length > 0 ? items[items.length - 1] : null;
    if (!fallbackRaw) {
      return baseMessage ?? createLatestMessageFallback(chatId);
    }
    const normalizedFallback = normalizeChatMessage(fallbackRaw, { ensureSeenByArray: true }) || fallbackRaw;
    const merged = {
      ...(baseMessage || {}),
      ...normalizedFallback,
    };
    const baseContent = typeof baseMessage?.content === "string" ? baseMessage.content.trim() : "";
    const fallbackContent = typeof normalizedFallback?.content === "string" ? normalizedFallback.content : "";
    merged.content = baseContent || fallbackContent || "No messages yet";
    if (!merged.timestamp) {
      merged.timestamp =
        normalizeTimestamp(
          normalizedFallback.timestamp ?? normalizedFallback.createdAt ?? normalizedFallback.sentAt
        ) || null;
    }
    return merged;
  } catch (error) {
    console.warn("Failed to hydrate latest message metadata from history", error);
    return baseMessage ?? createLatestMessageFallback(chatId);
  }
};

const getLatestMessage = async (chatId) => {
  if (!chatId) throw new Error("chatId is required");
  try {
    const response = await axios.get(
      `${CHAT_SERVICE_URL}/api/chat/${encodeURIComponent(chatId)}/latest-message`,
      withAuth()
    );
    const normalized = normalizeLatestMessageResponse(response?.data, chatId);
    if (hasTimestampMetadata(normalized)) {
      return normalized;
    }
    return hydrateLatestMessageFromHistory(chatId, normalized);
  } catch (error) {
    console.error("Failed to fetch latest chat message", error);
    return hydrateLatestMessageFromHistory(chatId);
  }
};

// Unified message sender for both direct chats and group channels
// Usage:
//  - sendMessage({ chatId, contentHtml, attachments, replyToMessageId })
//  - sendMessage({ groupId, channelId, contentHtml, attachments, replyToMessageId })
const sendMessage = async ({ chatId, groupId, channelId, contentHtml, attachments, replyToMessageId }) => {
  const payload = { content: contentHtml };
  if (Array.isArray(attachments) && attachments.length) {
    payload.attachments = attachments;
  }
  if (replyToMessageId !== undefined && replyToMessageId !== null && String(replyToMessageId).trim()) {
    payload.replyToMessageId = String(replyToMessageId).trim();
  }
  const config = withJsonAuth();
  if (chatId) {
    const response = await axios.post(
      `${CHAT_SERVICE_URL}/api/chat/${encodeURIComponent(chatId)}/message`,
      payload,
      config
    );
    return response.data;
  }
  if (groupId && channelId) {
    const response = await axios.post(
      `${CHAT_SERVICE_URL}/api/chat/groups/${encodeURIComponent(groupId)}/channels/${encodeURIComponent(channelId)}/messages`,
      payload,
      config
    );
    return response.data;
  }
  throw new Error("sendMessage requires either chatId or (groupId and channelId)");
};

// Comments APIs
async function addComment(messageId, contentHtml) {
  if (!messageId) throw new Error("messageId is required");
  const payload = { content: contentHtml };
  const response = await axios.post(
    `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/comments`,
    payload,
    withJsonAuth()
  );
  return response.data;
}

async function listComments(messageId, page = 0, size = 20) {
  if (!messageId) throw new Error("messageId is required");
  const response = await axios.get(
    `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/comments`,
    { ...withAuth(), params: { page, size } }
  );
  return response.data;
}

async function editComment(messageId, commentId, contentHtml) {
  if (!messageId) throw new Error("messageId is required");
  if (!commentId) throw new Error("commentId is required");
  const payload = { content: contentHtml };
  const response = await axios.patch(
    `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/comments/${encodeURIComponent(commentId)}`,
    payload,
    withJsonAuth()
  );
  return response.data;
}

async function deleteComment(messageId, commentId) {
  if (!messageId) throw new Error("messageId is required");
  if (!commentId) throw new Error("commentId is required");
  await axios.delete(
    `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/comments/${encodeURIComponent(commentId)}`,
    withAuth()
  );
  return true;
}

// Backward-compat wrappers (kept for now)
const sendDirectMessage = async (chatId, contentHtml) => sendMessage({ chatId, contentHtml });

export const chatService = {
  createDirectChat,
  createGroupChat,
  getMyChats,
  getChatMessages,
  getChatMessagesPage,
  getChatMessageCount,
  getLatestMessage,
  searchChatMessages,
  sendDirectMessage,
  sendMessage,
  async deleteMessage(messageId) {
    if (!messageId) throw new Error("messageId is required");
    const response = await axios.delete(
      `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}`,
      withAuth()
    );
    return response?.data ?? { id: messageId };
  },
  async editMessage(messageId, contentHtml) {
    if (!messageId) throw new Error("messageId is required");
    const payload = { content: contentHtml };
    const response = await axios.patch(
      `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}`,
      payload,
      withJsonAuth()
    );
    return response.data;
  },
  async addReaction(messageId, emoji) {
    if (!messageId) throw new Error("messageId is required");
    if (!emoji) throw new Error("emoji is required");
    const payload = { emoji };
    const response = await axios.post(
      `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/reactions`,
      payload,
      withJsonAuth()
    );
    return Array.isArray(response.data) ? response.data : [];
  },
  async removeReaction(messageId, emoji) {
    if (!messageId) throw new Error("messageId is required");
    if (!emoji) throw new Error("emoji is required");
    const response = await axios.delete(
      `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(emoji)}`,
      withAuth()
    );
    return Array.isArray(response.data) ? response.data : [];
  },
  async getReactions(messageId) {
    if (!messageId) throw new Error("messageId is required");
    const response = await axios.get(
      `${CHAT_SERVICE_URL}/api/chat/message/${encodeURIComponent(messageId)}/reactions`,
      withAuth()
    );
    return Array.isArray(response.data) ? response.data : [];
  },
  addComment,
  listComments,
  editComment,
  deleteComment,
};

export default chatService;
